import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  BarChart3, Download, FolderInput, Inbox, Loader2, Mail, Shield, PoundSterling,
} from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../ui/button";
import FirmMoneyBarCard from "../FirmMoneyBarCard";
import { FileMoverModal, MailboxAuditModal, SofSendsModal } from "../../pages/Dashboard";

/**
 * BM-H626 (26 Sep 2026): the Tools tab of the Directors Lounge.
 *
 * These were the "Director Tools" pop-up on the home page (opened from the
 * avatar menu's "Director Admin"). Vanessa: one Directors Lounge, "gated to me
 * and rebekah, the rest is noise". Same tools, same endpoints; the page that
 * holds this tab checks the director role, and the server checks it again.
 */
export default function DirectorTools() {
  const navigate = useNavigate();
  const [showMailboxAudit, setShowMailboxAudit] = useState(false);
  const [showSofSends, setShowSofSends] = useState(false);
  const [showFileMover, setShowFileMover] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hub/compliance/backfill-log`)
      .then((res) => { if (res.data?.timestamp) setBackfillResult(res.data); })
      .catch(() => {}); // nosilent: the last-run line is optional; the button still works
  }, []);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/hub/compliance/backfill-references`);
      setBackfillResult(res.data);
      toast.success(`Backfill complete: ${res.data.updated} updated`);
    } catch (err) {
      toast.error(errorMessage(err, "Backfill failed"));
    }
    setBackfilling(false);
  };

  return (
    <div className="space-y-4" data-testid="directors-lounge-tools">
      <FirmMoneyBarCard />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => navigate("/admin/tools")}
          className="gap-1.5" data-testid="dl-admin-panel-btn">
          <Shield className="w-3.5 h-3.5" /> Admin Panel
        </Button>
        {/* 30 Sep 2026: Vanessa — "director's lounge button, this must be
            gated". FeeEarnerRates.jsx was only reachable by typing its URL.
            Gating here is belt-and-braces: this whole page already 403s
            non-directors (see DirectorsLounge.jsx), and the target page's
            own canManageRates check (role === DIRECTOR || isAdmin) is the
            real enforcement, matching every other tool in this list. */}
        <Button size="sm" variant="outline" onClick={() => navigate("/finance/fee-earner-rates")}
          className="gap-1.5" data-testid="dl-fee-earner-rates-btn">
          <PoundSterling className="w-3.5 h-3.5" /> Fee Earner Rates
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate("/conflict-check-health")}
          className="gap-1.5" data-testid="dl-cc-health-btn">
          <BarChart3 className="w-3.5 h-3.5" /> CC Health
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowMailboxAudit(true)}
          className="gap-1.5" data-testid="dl-mailbox-audit-btn">
          <Inbox className="w-3.5 h-3.5" /> Mailbox Audit
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowSofSends(true)}
          className="gap-1.5" data-testid="dl-sof-sends-btn">
          <Mail className="w-3.5 h-3.5" /> SoF Sends
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowFileMover(true)}
          className="gap-1.5" data-testid="dl-file-mover-btn">
          <FolderInput className="w-3.5 h-3.5" /> File Mover
        </Button>
        <Button size="sm" variant="outline" onClick={runBackfill} disabled={backfilling}
          className="gap-1.5" data-testid="dl-backfill-btn">
          {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Backfill References
        </Button>
      </div>
      {backfillResult && (
        <p className="text-xs text-txt-muted" data-testid="dl-last-backfill">
          Last reference backfill:{" "}
          {backfillResult.timestamp ? format(parseISO(backfillResult.timestamp), "dd/MM/yyyy HH:mm") : "unknown"}
          {" "}({backfillResult.updated ?? "?"} updated)
        </p>
      )}
      {showMailboxAudit && <MailboxAuditModal onClose={() => setShowMailboxAudit(false)} />}
      {showSofSends && <SofSendsModal onClose={() => setShowSofSends(false)} />}
      {showFileMover && <FileMoverModal onClose={() => setShowFileMover(false)} />}
    </div>
  );
}
